import { LoadingButton } from '@mui/lab';
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Routes } from 'constants/pages';
import { useSegment } from 'common/hooks/segment';
import { Header } from 'layouts/SignUpTemplate/components/Header';
import { CreateCompanyPayload, inviteTeamMembers } from 'services/clients/user';
import { AddYourTeamInput } from './AddYourTeamInput';
import { determineFlow } from '../utility';

interface Props {
  companyType: CreateCompanyPayload['company_type_detail'];
}

export const AddYourTeam: React.FC<Props> = (props) => {
  const [emails, setEmails] = React.useState<string[]>([]);
  const { track } = useSegment();

  const navigate = useNavigate();
  const mutation = useMutation(inviteTeamMembers);

  let nextStep = Routes.Welcome;
  switch (determineFlow(props.companyType)) {
    case 'shipper':
      nextStep = Routes.Waitlist;
      break;
    case 'trucker':
    default:
      nextStep = Routes.Welcome;
  }

  return (
    <div className="w-full text-center w-full max-w-[568px] space-y-16">
      <Header header="Add your team!" subHeader="To give BlueCargo a spin, add a few coworkers." />
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <AddYourTeamInput onEmailsChange={setEmails} error={(mutation.error as any)?.message} />
      <div className="space-y-4">
        <LoadingButton
          variant="contained"
          type="submit"
          loading={mutation.isLoading}
          disabled={emails.length === 0}
          size="large"
          className="w-full mt-14"
          onClick={() =>
            mutation.mutate(emails, {
              onSuccess: () => {
                track(
                  'User invited team members',
                  {
                    invitedEmails: emails,
                    inviteCount: emails.length,
                    success: true,
                    error: '',
                  },
                  { includeUser: true }
                );
                track(
                  'New Account Created',
                  {
                    invitedTeamMembers: emails,
                    success: true,
                    error: '',
                  },
                  { includeUser: true, includeCompany: true }
                );
                navigate(nextStep);
              },
              // eslint-disable-next-line
              onError: (error: any) => {
                // eslint-disable-next-line no-console
                console.error(error);
                track(
                  'User invited team members',
                  {
                    invitedEmails: emails,
                    inviteCount: emails.length,
                    success: false,
                    error: error.message,
                  },
                  { includeUser: true }
                );
                track(
                  'New Account Created',
                  {
                    invitedTeamMembers: emails,
                    success: false,
                    error: error.message,
                  },
                  { includeUser: true, includeCompany: true }
                );
              },
            })
          }
        >
          <h3>Invite Team</h3>
        </LoadingButton>
        <div>
          <Link
            to={Routes.Home}
            onClick={() => {
              track(
                'New Account Created',
                {
                  invitedTeamMembers: [],
                  success: true,
                  error: '',
                },
                { includeUser: true, includeCompany: true }
              );
            }}
          >
            Skip this step
          </Link>
        </div>
      </div>
    </div>
  );
};
